import type {
  DimensionAnnotation,
  ValidationMessage,
  WindZoneGeometry,
  ZoneResult,
} from '../../gen/ejot/roofing/v1/types';
import { polygonSvgPath } from '../../shared/polygonRings';
import { ActionableDiagnostic } from '../diagnostics/ActionableDiagnostic';
import { CoordinateIndicator } from '../../shared/CoordinateIndicator';
import type { LayerVisibility } from '../roof-plan-editor/render';
import { zoneEngineeringLabel } from './zonePresentation';

interface WindZonePlanProps {
  zones: readonly WindZoneGeometry[];
  dimensions: readonly DimensionAnnotation[];
  results?: readonly ZoneResult[];
  messages?: readonly ValidationMessage[];
  layers: LayerVisibility;
  selectedZoneId?: string;
  onZoneSelect?: (zoneId: string) => void;
}

interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface DimensionLayout {
  dimension: DimensionAnnotation;
  sourceAX: number;
  sourceAY: number;
  sourceBX: number;
  sourceBY: number;
  ax: number;
  ay: number;
  bx: number;
  by: number;
  textX: number;
  textY: number;
  textRotationDegrees: number;
  label: string;
}

type DimensionSide = 'top' | 'right' | 'bottom' | 'left';
interface OccupiedInterval { min: number; max: number }

const EMPTY_BOUNDS: Bounds = { minX: 0, minY: 0, maxX: 1, maxY: 1 };

/**
 * CAD-style presentation of authoritative backend zone geometry.
 *
 * This component deliberately performs no engineering derivation. Zone
 * polygons, labels, dimensions and result text all come from backend contracts.
 * Arithmetic is limited to drawing coordinates, visual spacing and unit/text
 * formatting.
 */
export function WindZonePlan({
  zones,
  dimensions,
  results = [],
  messages = [],
  layers,
  selectedZoneId,
  onZoneSelect,
}: WindZonePlanProps) {
  if (zones.length === 0) {
    return (
      <div className="viewer-overlay" role="status" aria-live="polite">
        <ActionableDiagnostic
          messages={messages}
          fallback="No authoritative wind-zone geometry is available for the current result. Complete the required geometry and wind inputs so the backend can generate zones."
        />
      </div>
    );
  }

  const visibleDimensions = dimensions.filter((dimension) => dimension.visible !== false);
  const roofBounds = windPlanBounds(zones, []);
  const roofSpanX = Math.max(roofBounds.maxX - roofBounds.minX, 1);
  const roofSpanY = Math.max(roofBounds.maxY - roofBounds.minY, 1);
  const drawingSpan = Math.max(roofSpanX, roofSpanY);
  const dimensionLayouts = layoutDimensionsOutsideRoof(visibleDimensions, roofBounds, drawingSpan, zones);
  const bounds = displayBounds(zones, dimensionLayouts);
  const spanX = Math.max(bounds.maxX - bounds.minX, 1);
  const spanY = Math.max(bounds.maxY - bounds.minY, 1);
  const padding = drawingSpan * 0.06;
  const viewMinX = bounds.minX - padding;
  const viewMinY = bounds.minY - padding;
  const viewWidth = spanX + 2 * padding;
  const viewHeight = spanY + 2 * padding;

  const zoneLayerVisible = layers['wind-zones'] !== false;
  const labelLayerVisible = layers['zone-labels'] !== false;
  const dimensionLayerVisible = layers['zone-dimensions'] !== false;
  const resultByZone = new Map(results.map((result) => [result.zone_id ?? '', result]));

  return (
    <div className="wind-zone-plan" aria-label="Authoritative wind-zone plan">
      <svg
        className="wind-zone-plan-svg"
        viewBox={`${viewMinX} ${viewMinY} ${viewWidth} ${viewHeight}`}
        role="img"
        aria-label="Colour-coded wind-zone geometry with backend dimensions and pressure values"
      >
        <defs>
          <marker
            id="wind-plan-arrow"
            markerWidth="6"
            markerHeight="6"
            refX="3"
            refY="3"
            orient="auto-start-reverse"
            markerUnits="strokeWidth"
          >
            <path d="M 3 0 L 3 6" fill="none" stroke="var(--ejot-red)" strokeWidth="1.2" />
          </marker>
        </defs>

        {zoneLayerVisible && zones.map((zone) => {
          const path = polygonSvgPath(zone.outline);
          if (!path) return null;
          const colour = semanticZoneColour(zone.colour_token);
          const selected = zone.id === selectedZoneId;
          return (
            <path
              key={zone.id}
              d={path}
              fill={colour}
              fillRule="evenodd"
              clipRule="evenodd"
              fillOpacity={selected ? 0.66 : 0.48}
              stroke={selected ? 'var(--text-primary)' : colour}
              strokeWidth={selected ? 3 : 1.5}
              vectorEffect="non-scaling-stroke"
              data-zone-id={zone.id}
              tabIndex={onZoneSelect ? 0 : undefined}
              role={onZoneSelect ? 'button' : undefined}
              aria-label={onZoneSelect ? `Select wind zone ${zone.display_label || zone.id}` : undefined}
              onClick={() => zone.id && onZoneSelect?.(zone.id)}
              onKeyDown={(event) => {
                if (!zone.id || !onZoneSelect) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onZoneSelect(zone.id);
                }
              }}
            />
          );
        })}

        {labelLayerVisible && zones.map((zone) => {
          if (!zone.label_anchor) return null;
          const result = resultByZone.get(zone.id ?? '');
          const label = zoneEngineeringLabel(zone, result);
          const x = zone.label_anchor.x ?? 0;
          const y = zone.label_anchor.y ?? 0;
          return (
            <g key={`label-${zone.id}`} pointerEvents="none">
              <text
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="var(--text-primary)"
                fontFamily="Roboto Condensed, Arial, sans-serif"
                fontSize={drawingSpan * 0.018}
                fontWeight={700}
                paintOrder="stroke"
                stroke="var(--surface-card)"
                strokeWidth={drawingSpan * 0.0025}
              >
                {label.zoneText}
              </text>
              {label.pressureText && (
                <text
                  x={x}
                  y={y + drawingSpan * 0.022}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="var(--text-secondary)"
                  fontFamily="Roboto Condensed, Arial, sans-serif"
                  fontSize={drawingSpan * 0.011}
                  fontWeight={600}
                  paintOrder="stroke"
                  stroke="var(--surface-card)"
                  strokeWidth={drawingSpan * 0.0025}
                >
                  {label.pressureText}
                </text>
              )}
            </g>
          );
        })}

        {dimensionLayerVisible && dimensionLayouts.map((layout) => (
          <g key={layout.dimension.id} data-dimension-id={layout.dimension.id}>
            <line
              x1={layout.sourceAX}
              y1={layout.sourceAY}
              x2={layout.ax}
              y2={layout.ay}
              stroke="var(--ejot-red)"
              strokeOpacity={0.65}
              strokeWidth={0.9}
              vectorEffect="non-scaling-stroke"
            />
            <line
              x1={layout.sourceBX}
              y1={layout.sourceBY}
              x2={layout.bx}
              y2={layout.by}
              stroke="var(--ejot-red)"
              strokeOpacity={0.65}
              strokeWidth={0.9}
              vectorEffect="non-scaling-stroke"
            />
            <line
              x1={layout.ax}
              y1={layout.ay}
              x2={layout.bx}
              y2={layout.by}
              stroke="var(--ejot-red)"
              strokeWidth={1.2}
              vectorEffect="non-scaling-stroke"
              markerStart="url(#wind-plan-arrow)"
              markerEnd="url(#wind-plan-arrow)"
            />
            <text
              x={layout.textX}
              y={layout.textY}
              textAnchor="middle"
              dominantBaseline="middle"
              transform={layout.textRotationDegrees === 0
                ? undefined
                : `rotate(${layout.textRotationDegrees} ${layout.textX} ${layout.textY})`}
              fill="var(--ejot-red)"
              fontFamily="Roboto Condensed, Arial, sans-serif"
              fontSize={drawingSpan * 0.011}
              fontWeight={600}
              paintOrder="stroke"
              stroke="var(--surface-card)"
              strokeWidth={drawingSpan * 0.004}
            >
              {layout.label}
            </text>
          </g>
        ))}
      </svg>
      <CoordinateIndicator mode="2d" visible={layers.axes !== false} />

      <div className="wind-zone-plan-legend" aria-label="Wind-zone legend">
        {uniqueZoneLegend(zones).map((entry) => (
          <span className="wind-zone-plan-legend-item" key={entry.token}>
            <span className="wind-zone-plan-swatch" style={{ background: semanticZoneColour(entry.token) }} />
            Zone {entry.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Places authoritative dimensions into deterministic CAD-style lanes.
 *
 * Earlier code incremented a lane for every dimension in input order. That
 * avoided some exact overlaps but produced unnecessarily deep stacks and still
 * let long labels collide with neighbouring dimensions. The V2 solver assigns
 * the lowest available lane whose projected line/text interval does not collide
 * with an existing occupant. Zone ΔX/ΔY extents are prioritised nearest the
 * roof, followed by source-normal offsets and overall/edge dimensions.
 */
export function layoutDimensionsOutsideRoof(
  dimensions: readonly DimensionAnnotation[],
  roofBounds: Bounds,
  drawingSpan: number,
  zones: readonly WindZoneGeometry[] = [],
): DimensionLayout[] {
  const centreX = (roofBounds.minX + roofBounds.maxX) / 2;
  const centreY = (roofBounds.minY + roofBounds.maxY) / 2;
  const occupancy: Record<DimensionSide, OccupiedInterval[][]> = {
    top: [], right: [], bottom: [], left: [],
  };
  const baseOffset = drawingSpan * 0.035;
  const laneGap = drawingSpan * 0.028;
  const textGap = drawingSpan * 0.012;
  const collisionGap = drawingSpan * 0.014;
  const layouts: DimensionLayout[] = [];

  const ordered = [...dimensions].sort((a, b) => {
    const priority = dimensionPriority(a) - dimensionPriority(b);
    if (priority !== 0) return priority;
    return (a.id ?? '').localeCompare(b.id ?? '');
  });

  for (const dimension of ordered) {
    const internal = layoutZoneExtentInside(dimension, zones, drawingSpan);
    if (internal) {
      layouts.push(internal);
      continue;
    }
    const [a, b] = dimension.anchors ?? [];
    if (!a || !b) continue;
    const label = dimensionDisplayLabel(dimension);
    if (!label) continue;

    const sourceAX = a.x ?? 0;
    const sourceAY = a.y ?? 0;
    const sourceBX = b.x ?? 0;
    const sourceBY = b.y ?? 0;
    const dx = sourceBX - sourceAX;
    const dy = sourceBY - sourceAY;
    const segmentLength = Math.hypot(dx, dy);
    if (segmentLength <= Number.EPSILON) continue;

    const midX = (sourceAX + sourceBX) / 2;
    const midY = (sourceAY + sourceBY) / 2;
    let outwardX = midX - centreX;
    let outwardY = midY - centreY;
    let outwardLength = Math.hypot(outwardX, outwardY);

    if (outwardLength <= drawingSpan * 1e-9) {
      const normalAX = -dy / segmentLength;
      const normalAY = dx / segmentLength;
      const normalBX = -normalAX;
      const normalBY = -normalAY;
      const scoreA = Math.hypot(midX + normalAX * baseOffset - centreX, midY + normalAY * baseOffset - centreY);
      const scoreB = Math.hypot(midX + normalBX * baseOffset - centreX, midY + normalBY * baseOffset - centreY);
      outwardX = scoreA >= scoreB ? normalAX : normalBX;
      outwardY = scoreA >= scoreB ? normalAY : normalBY;
      outwardLength = 1;
    }
    outwardX /= outwardLength;
    outwardY /= outwardLength;

    const side = dominantSide(outwardX, outwardY);
    const projectedA = side === 'top' || side === 'bottom' ? sourceAX : sourceAY;
    const projectedB = side === 'top' || side === 'bottom' ? sourceBX : sourceBY;
    const approximateTextWidth = Math.max(drawingSpan * 0.045, label.length * drawingSpan * 0.0082);
    const interval: OccupiedInterval = {
      min: Math.min(projectedA, projectedB, (projectedA + projectedB) / 2 - approximateTextWidth / 2) - collisionGap,
      max: Math.max(projectedA, projectedB, (projectedA + projectedB) / 2 + approximateTextWidth / 2) + collisionGap,
    };
    const lane = claimDimensionLane(occupancy[side], interval);
    const shift = baseOffset + lane * laneGap;
    const shiftX = outwardX * shift;
    const shiftY = outwardY * shift;

    const ax = sourceAX + shiftX;
    const ay = sourceAY + shiftY;
    const bx = sourceBX + shiftX;
    const by = sourceBY + shiftY;
    const shiftedMidX = (ax + bx) / 2;
    const shiftedMidY = (ay + by) / 2;
    const textX = shiftedMidX + outwardX * textGap;
    const textY = shiftedMidY + outwardY * textGap;
    const textRotationDegrees = uprightTextAngle((Math.atan2(dy, dx) * 180) / Math.PI);

    layouts.push({
      dimension,
      sourceAX,
      sourceAY,
      sourceBX,
      sourceBY,
      ax,
      ay,
      bx,
      by,
      textX,
      textY,
      textRotationDegrees,
      label,
    });
  }

  return layouts;
}

function layoutZoneExtentInside(
  dimension: DimensionAnnotation,
  zones: readonly WindZoneGeometry[],
  drawingSpan: number,
): DimensionLayout | undefined {
  const id = dimension.id ?? '';
  const horizontal = id.includes('-extent-x');
  const vertical = id.includes('-extent-y');
  if (!horizontal && !vertical) return undefined;
  const objectId = dimension.measured_object_ids?.[0];
  const zone = zones.find((candidate) => candidate.id === objectId);
  const points = zone?.outline?.points ?? [];
  if (!zone || points.length < 3) return undefined;
  const xs = points.map((point) => point.x ?? 0);
  const ys = points.map((point) => point.y ?? 0);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const width = maxX - minX;
  const height = maxY - minY;
  if (width <= Number.EPSILON || height <= Number.EPSILON) return undefined;
  const label = dimensionDisplayLabel(dimension);
  if (!label) return undefined;
  const textGap = drawingSpan * 0.006;

  if (horizontal) {
    const y = minY + height * 0.75;
    return {
      dimension,
      sourceAX: minX,
      sourceAY: minY,
      sourceBX: maxX,
      sourceBY: minY,
      ax: minX,
      ay: y,
      bx: maxX,
      by: y,
      textX: (minX + maxX) / 2,
      textY: y - textGap,
      textRotationDegrees: 0,
      label,
    };
  }

  const x = minX + width * 0.75;
  return {
    dimension,
    sourceAX: minX,
    sourceAY: minY,
    sourceBX: minX,
    sourceBY: maxY,
    ax: x,
    ay: minY,
    bx: x,
    by: maxY,
    textX: x + textGap,
    textY: (minY + maxY) / 2,
    textRotationDegrees: 90,
    label,
  };
}

function dimensionDisplayLabel(dimension: DimensionAnnotation): string {
  if ((dimension.id ?? '').includes('-extent-x') || (dimension.id ?? '').includes('-extent-y')) {
    return formatDimensionMetres(dimension.value_millimetres);
  }
  return (dimension.label?.trim() || formatDimensionMetres(dimension.value_millimetres))
    .replace(/^Δ[XY]\s*/i, '');
}

function dimensionPriority(dimension: DimensionAnnotation): number {
  const id = dimension.id ?? '';
  if (id.includes('-extent-x') || id.includes('-extent-y')) return 0;
  if (id.includes('outer-offset') || id.includes('inner-offset')) return 1;
  if (id.includes('edge-length')) return 2;
  return 3;
}

function claimDimensionLane(lanes: OccupiedInterval[][], interval: OccupiedInterval): number {
  for (let lane = 0; lane < lanes.length; lane += 1) {
    const currentLane = lanes[lane];
    if (!currentLane) continue;
    if (currentLane.every((occupied) => interval.max < occupied.min || interval.min > occupied.max)) {
      currentLane.push(interval);
      return lane;
    }
  }
  lanes.push([interval]);
  return lanes.length - 1;
}

function dominantSide(x: number, y: number): DimensionSide {
  if (Math.abs(x) >= Math.abs(y)) return x < 0 ? 'left' : 'right';
  return y < 0 ? 'top' : 'bottom';
}

function uprightTextAngle(angle: number): number {
  let value = angle;
  while (value > 90) value -= 180;
  while (value < -90) value += 180;
  return Math.abs(value) < 1e-9 ? 0 : value;
}

export function formatDimensionMetres(valueMillimetres: number | undefined): string {
  if (valueMillimetres === undefined || !Number.isFinite(valueMillimetres)) return '';
  return `${(valueMillimetres / 1000).toFixed(2)} m`;
}

export function windPlanBounds(
  zones: readonly WindZoneGeometry[],
  dimensions: readonly DimensionAnnotation[],
): Bounds {
  const points = [
    ...zones.flatMap((zone) => zone.outline?.points ?? []),
    ...dimensions.flatMap((dimension) => dimension.anchors ?? []),
  ];
  if (points.length === 0) return EMPTY_BOUNDS;

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    const x = point.x ?? 0;
    const y = point.y ?? 0;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  return { minX, minY, maxX, maxY };
}

function displayBounds(zones: readonly WindZoneGeometry[], dimensions: readonly DimensionLayout[]): Bounds {
  const zoneBounds = windPlanBounds(zones, []);
  let minX = zoneBounds.minX;
  let minY = zoneBounds.minY;
  let maxX = zoneBounds.maxX;
  let maxY = zoneBounds.maxY;
  for (const dimension of dimensions) {
    minX = Math.min(minX, dimension.ax, dimension.bx, dimension.textX);
    minY = Math.min(minY, dimension.ay, dimension.by, dimension.textY);
    maxX = Math.max(maxX, dimension.ax, dimension.bx, dimension.textX);
    maxY = Math.max(maxY, dimension.ay, dimension.by, dimension.textY);
  }
  return { minX, minY, maxX, maxY };
}

function semanticZoneColour(token: string | undefined): string {
  if (!token || !/^zone-[a-z0-9-]+$/.test(token)) return 'var(--zone-interior)';
  return `var(--${token})`;
}

function uniqueZoneLegend(zones: readonly WindZoneGeometry[]) {
  const values = new Map<string, string>();
  for (const zone of zones) {
    const token = zone.colour_token || 'zone-interior';
    if (!values.has(token)) values.set(token, zone.display_label || token);
  }
  return [...values.entries()].map(([token, label]) => ({ token, label }));
}